import React from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import map from 'lodash/map';
import some from 'lodash/some';
import moment from 'moment';
import { I18n } from 'react-redux-i18n';

import Select from 'components/Select';
import LineGraph from 'components/Charts/LineGraph';
import ExportButton from 'components/Export';
import CardHeader from 'components/CardHeader';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  background-color: white;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .graphContainer {
    margin-left: -2rem;
  }

  .graphSelect {
    width: 400px;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      width: calc(100vw - 2rem);
    }
  }

  .rhcTooltipContainer {
    min-width: 350px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact};
    font-size: 12px;
    width: 100%;
    margin-bottom: 10px;

    > p {
      color: #abb5be;
      font-family: ${(props) => props.theme.fonts.sfcompact_text};
      font-weight: 600;
    }
  }

  .rhcTooltipValues {
    margin-left: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 75%;

    p {
      margin-left: 5px;
    }
  }

  .rhcTooltipValues > * {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 33%;
    text-align: center;
  }

  .rhcTooltipValues > div:not(:last-child) {
    margin-right: 15px;
  }

  .rhcTooltip {
    padding: 12px;
    min-width: 250px;
  }

  .rhcDate {
    color: #abb5be;
    text-transform: uppercase;
  }

  .rhcLabel {
    text-transform: uppercase;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
  }
`;

const SelectContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export default class CompareLineGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tagList: [],
      rivalsOptions: this.getRivalsOptions(props.clientWines, props.data)
    };
  }

  componentDidUpdate(prevProps) {
    const { clientWines, data } = this.props;
    if (
      JSON.stringify(prevProps.clientWines) !== JSON.stringify(clientWines) ||
      JSON.stringify(prevProps.data) !== JSON.stringify(data)
    ) {
      const rivalsOptions = this.getRivalsOptions(clientWines, data);
      this.setState({ tagList: [], rivalsOptions });
    }
  }

  getRivalsOptions(clientWinesOptions, data) {
    const rivalsMap = reduce(
      data,
      (acc, { name }) => {
        if (!some(clientWinesOptions, (cw) => cw.value === name)) {
          acc[name] = { value: name, label: name };
        }
        return acc;
      },
      {}
    );
    return map(Object.keys(rivalsMap), (key) => rivalsMap[key]);
  }

  shouldBeDisplayed(name) {
    const { clientWines } = this.props;
    const { tagList } = this.state;
    return (
      some(clientWines, (cw) => cw.label === name) ||
      some(tagList, (tag) => tag.label === name)
    );
  }

  formatdata() {
    const { data = [], type } = this.props;
    const formattedData = reduce(
      data,
      (acc, { year, dn, name, date }) => {
        const item = {
          name:
            type === 'restaurants' || type === 'caves'
              ? year === 9999
                ? I18n.t('travel.now')
                : year.toString()
              : moment(date).format('DD/MM/YYYY'),
          value: dn,
          wine: name
        };

        if (this.shouldBeDisplayed(name)) {
          if (!acc.hasOwnProperty(name)) {
            acc[name] = [];
          }
          acc[name] = [...acc[name], item];
        }
        return acc;
      },
      {}
    );

    return map(Object.keys(formattedData), (key) => formattedData[key]);
  }

  getLabels(data) {
    if (isEmpty(data)) return [];
    return reduce(
      data,
      (acc, curr) => {
        if (!isEmpty(curr)) {
          acc.push(curr[0].wine);
        }
        return acc;
      },
      []
    );
  }

  orderData() {
    const { clientWines } = this.props;
    const formattedData = [...this.formatdata()];

    if (formattedData.length > 0) {
      formattedData.forEach((set, index) => {
        if (
          clientWines.map((item) => item.label).includes(get(set, '[0].wine'))
        ) {
          formattedData.splice(index, 1);
          formattedData.unshift(set);
        }
      });
    }
    let setNames = [];
    let wines = [];
    formattedData.forEach((set) => {
      if (set && set.length > 0) {
        setNames.push(...set.map((item) => item.name));
        wines.push(set[0].wine);
      }
    });
    setNames = [...new Set(setNames)].sort(function (a, b) {
      if ((a && String(a).includes('/')) || (b && String(b).includes('/'))) {
        return (
          moment(a, 'DD/MM/YYYY').toDate() - moment(b, 'DD/MM/YYYY').toDate()
        );
      }
      return String(a).localeCompare(String(b));
    });

    formattedData.forEach((set, setIndex) => {
      setNames.forEach((name, index) => {
        if (!set[index] || set[index].name !== name) {
          set.splice(index, 0, {
            name,
            wine: wines[setIndex],
            value: null
          });
        }
      });
    });
    return formattedData;
  }

  renderLineGraph() {
    const formattedData = this.orderData();
    const labels = this.getLabels(formattedData);

    return (
      <div className="graphContainer">
        <LineGraph
          height={400}
          data={formattedData}
          labels={labels}
          yAxisProps={{
            width: 90,
            scale: 'linear',
            domain: [
              0,
              (dataMax) => Number(dataMax + dataMax / 10 + 5).toFixed(0)
            ]
          }}
          yTickFormatter={(value) => Math.round(value)}
          tooltipOffset={{ x: 0, y: 50 }}
          colors={['#e25d34', '#2B3D54', '#a5aaaf', '#B1B7BD', '#C9D0D6']}
        />
      </div>
    );
  }

  renderSelectSets() {
    const { getFirstForExport } = this.props;
    const { tagList, rivalsOptions } = this.state;

    return (
      <SelectContainer className="ignored-export">
        <Select
          placeholder={I18n.t('placeholderRivals')}
          isMulti
          value={tagList}
          dropdownProps={{
            styles: `margin-left:20px;margin-right:20px;overflowY:auto;`
          }}
          className="graphSelect"
          customStyles={{
            valueContainer: {
              overflowY: 'auto',
              height: '45px'
            }
          }}
          onChange={(value) => {
            getFirstForExport(get(value, '[0].label'));
            this.setState({
              tagList:
                value && value.length > 5 ? value.slice(1, value.length) : value
            });
          }}
          options={rivalsOptions}
        />
      </SelectContainer>
    );
  }

  render() {
    const { optionsExportXls, historyMarkets, selectedIndicator } = this.props;

    return (
      <Container id="synthese-evolution-dn">
        <CardHeader
          title={I18n.t('synthesisDistribution.detail.titleGraph')}
          fontSize="1.25rem"
          iconRight="right"
          rightComponents={
            <ExportButton
              name={`${get(this.state.selectedIndicator, 'label')}-${get(
                this.props,
                'selectedMarket.label'
              )}-${get(this.props, 'selectedEntity.label')}-${I18n.t(
                'synthesisDistribution.detail.titleGraph'
              )}`}
              targetElement="#synthese-evolution-dn"
              jpg
              pdf
              xls
              pdfOrientation="landscape"
              path="/distribution/synthesis/xls?"
              options={optionsExportXls}
              newApi
            />
          }
        />
        <Wrapper>
          <div className="graphTopText">
            {get(selectedIndicator, 'value') === 'websites'
              ? I18n.t('synthesisDistribution.detail.subTitleGraphWeb')
              : `${I18n.t(
                  'synthesisDistribution.detail.subTitleGraph'
                )} : ${historyMarkets}`}
          </div>
          {this.renderLineGraph()}
          {this.renderSelectSets()}
        </Wrapper>
      </Container>
    );
  }
}
